import { Controller } from 'react-hook-form';
import { Option } from '@/legacy/types';
import { Select } from '@/legacy/components/ui/Form/Select/primitives';
import { useTranslation } from 'react-i18next';
import { formLabelsTradPrefix, formPlaceholderTradPrefix } from '@/legacy/i18n';
import React from 'react';
import { TriggerVariant } from '../types';

export type SelectorProps = {
	options: Option[];
	isLoadingOptions: boolean;
	name: string;
	placeholder?: string;
	label?: string;
	tooltipId?: string;
	tooltipText?: string;
	required?: boolean;
	searchable?: boolean;
	defaultValue?: string;
	infoCopy?: React.ReactNode;
	showScrollbar?: boolean;
	spacing?: boolean;
	endAdornment?: React.ReactNode;
	triggerVariant?: TriggerVariant;
	/** Called every time an option is selected, even if value doesn't change */
	onSelect?: (v: string | number) => void;
	/** Custom display value for the trigger button, overrides the label lookup */
	displayValue?: string;
	showErrorMessage?: boolean;
	helperText?: string | null;
	disabled?: boolean;
};

const Selector = ({
	options,
	isLoadingOptions,
	name,
	placeholder = `${formPlaceholderTradPrefix}.select_one_option`,
	label,
	tooltipId,
	tooltipText,
	required,
	searchable,
	defaultValue,
	infoCopy,
	showScrollbar = true,
	spacing = true,
	endAdornment,
	triggerVariant = 'button',
	onSelect,
	displayValue,
	showErrorMessage = true,
	helperText,
	disabled = false,
}: SelectorProps) => {
	const { t } = useTranslation();

	return (
		<Controller
			name={name}
			defaultValue={defaultValue}
			render={({ field, fieldState: { error: fieldError } }) => {
				const errorMessage = Array.isArray(fieldError)
					? fieldError.find((error) => error)?.message
					: fieldError?.message;

				const resolvedOptions =
					!required && options.length > 0 && field.value
						? [
								{
									label: t(`${formLabelsTradPrefix}.deselect`),
									value: null,
									renderLabel: (
										<span className='text-tertiary italic'>
											{t(`${formLabelsTradPrefix}.deselect`)}
										</span>
									),
								},
								...options,
							]
						: options;

				return (
					<Select.Root
						options={resolvedOptions}
						value={field.value}
						onChange={field.onChange}
						onSelect={onSelect}
						reorderSelectedToTop={!!required}
						floatingUiProps={{
							strategy: 'fixed',
						}}
					>
						{/* temporal spacing prop to avoid breaking changes spacing should be handle by container component */}
						<div className={spacing ? 'mb-6' : ''}>
							{label && (
								<Select.Label
									label={label}
									name={name}
									tooltipId={tooltipId}
									tooltipText={tooltipText}
									required={required}
									endAdornment={endAdornment}
									infoCopy={infoCopy}
								/>
							)}
							<Select.Trigger
								placeholder={t(placeholder)}
								fieldError={errorMessage}
								variant={triggerVariant}
								displayValue={displayValue}
								disabled={disabled}
							/>
							<Select.HelperText error={showErrorMessage ? errorMessage : undefined}>
								{helperText}
							</Select.HelperText>
							<Select.Positioner className='w-full'>
								<Select.Panel isLoadingOptions={isLoadingOptions}>
									<Select.OptionsList
										searchable={triggerVariant !== 'combobox' && searchable}
										showScrollbar={showScrollbar}
									/>
								</Select.Panel>
							</Select.Positioner>
						</div>
					</Select.Root>
				);
			}}
		/>
	);
};

export default Selector;
